import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import queryKey from "../../enumerations/queryKey";
import {
  EXCLUDE_DRAMA_ID_LIMIT,
  apiGetDramaList,
  apiGetPlayUrl,
  apiGetRandomDramaList,
  apiGetTheaterCategories,
  apiGetTheaterHome502,
  apiGetTheaterRankings,
  apiToggleDramaLikes,
  unwrapTheaterData,
} from "./service";

export const useGetTheaterHome502 = (options = {}) => {
  return useQuery(
    [queryKey.THEATER_HOME_502],
    () => apiGetTheaterHome502().then((res) => unwrapTheaterData(res) || {}),
    {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
};

export const useGetRandomDramaList = (seed, options = {}) => {
  const queryClient = useQueryClient();
  // Dramas seen in finished rounds; excluded so a new seed does not replay them.
  const excludeDramaIdsRef = useRef([]);
  // Dramas seen in the current round. The seed already keeps pages from
  // overlapping, so these are only promoted once the round ends.
  const currentRoundIdsRef = useRef([]);
  const seedRef = useRef(seed);

  useEffect(() => {
    if (!seed) return undefined;

    if (seedRef.current !== seed) {
      excludeDramaIdsRef.current = [
        ...excludeDramaIdsRef.current,
        ...currentRoundIdsRef.current,
      ].slice(-EXCLUDE_DRAMA_ID_LIMIT);
      currentRoundIdsRef.current = [];
      seedRef.current = seed;
    }

    // Drop previous visits' caches; keep current seed so remount won't refetch.
    queryClient.removeQueries({
      queryKey: [queryKey.SHORTS_RANDOM_DRAMA_LIST],
      predicate: (query) => query.queryKey[1] !== seed,
    });

    return undefined;
  }, [seed, queryClient]);

  return useInfiniteQuery(
    [queryKey.SHORTS_RANDOM_DRAMA_LIST, seed],
    ({ pageParam = 1 }) =>
      apiGetRandomDramaList({
        page: pageParam,
        seed,
        excludeDramaIds: excludeDramaIdsRef.current,
      }).then((res) => {
        const payload = unwrapTheaterData(res) || {};
        const list = payload.data || [];

        for (const item of list) {
          if (item?.drama_id && !currentRoundIdsRef.current.includes(item.drama_id)) {
            currentRoundIdsRef.current.push(item.drama_id);
          }
        }

        return {
          list,
          current_page: payload.current_page || pageParam,
          last_page: payload.last_page || pageParam,
        };
      }),
    {
      enabled: !!seed,
      getNextPageParam: (lastPage) => {
        if (!lastPage) return undefined;
        if (lastPage.current_page >= lastPage.last_page) return undefined;
        return lastPage.current_page + 1;
      },
      staleTime: Infinity,
      cacheTime: 5 * 60 * 1000,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
};

export const useGetTheaterCategories = (options = {}) => {
  return useQuery(
    [queryKey.THEATER_CATEGORIES],
    () =>
      apiGetTheaterCategories().then((res) => {
        const data = unwrapTheaterData(res);
        return Array.isArray(data) ? data : [];
      }),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
};

export const useGetTheaterRankings = (type, options = {}) => {
  return useQuery(
    [queryKey.THEATER_RANKINGS, type],
    () =>
      apiGetTheaterRankings({ type }).then((res) => {
        const payload = unwrapTheaterData(res) || {};
        return {
          type: payload.type || type,
          title: payload.title || "",
          list: Array.isArray(payload.list) ? payload.list : [],
        };
      }),
    {
      enabled: !!type,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
};

export const useGetDramaList = (filters = {}, options = {}) => {
  const typeId = filters?.typeId;
  const isCompleted = !!filters?.isCompleted;

  return useInfiniteQuery(
    [queryKey.THEATER_DRAMA_LIST, typeId ?? "all", isCompleted ? 1 : 0],
    ({ pageParam = 1 }) =>
      apiGetDramaList({
        page: pageParam,
        ...(typeId != null && typeId !== "" ? { type_id: typeId } : {}),
        ...(isCompleted ? { is_completed: 1 } : {}),
      }).then((res) => {
        const payload = unwrapTheaterData(res) || {};
        return {
          list: payload.data || [],
          current_page: payload.current_page || pageParam,
          last_page: payload.last_page || pageParam,
        };
      }),
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage) return undefined;
        if (lastPage.current_page >= lastPage.last_page) return undefined;
        return lastPage.current_page + 1;
      },
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
};

export const useGetPlayUrl = () => {
  return useMutation(({ episode_id }) =>
    apiGetPlayUrl({ episode_id }).then((res) => unwrapTheaterData(res)),
  );
};

export const useToggleDramaLikes = () => {
  return useMutation(({ episode_id }) =>
    apiToggleDramaLikes({ episode_id }).then((res) => unwrapTheaterData(res)),
  );
};
